import './Form.scss'
import { useForm } from 'react-hook-form'
import { Field, Input } from '@fluentui/react-components'
import { toast } from 'react-toastify'
import { Button } from '../Button'
import { RolesAssignmentComponent } from '../SubscriptionControl/RolesAssignmentComponent'
import { useCallback } from 'react'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useGetSearchParam } from '@/hooks/useGetSearchParams'
import { deriveRoleIdsFromRoleNumber, deriveRoleNumberFromRoleIds, roleCanBeAssigned, RolesArray } from '@/util/roles'
import { useQueryClient } from '@tanstack/react-query'
import { useSessionStorage } from '@/hooks'

type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']
type Roles = RestSchemas['DataModellingPlatform.Common.Roles']

export type UserForm = {
  email: string
  givenName: string
  familyName: string
  roleIdsArray: number[]
}

type AddUserToOrgFormProps = {
  onSubmit: () => void
  edit?: boolean
  selectedUser?: MessagesSubscriptionsOrganisationUser
  organisationId?: number
  refetchOrganisationUsers?: () => void
}

export const AddUserToOrgForm = ({
  onSubmit,
  edit,
  selectedUser,
  organisationId,
  refetchOrganisationUsers,
}: AddUserToOrgFormProps) => {
  const queryClient = useQueryClient()
  const { putSubscriptionsApiAddUser, postSubscriptionsApiUpdateUser } = createRestClient()

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    getValues,
  } = useForm<UserForm>({
    defaultValues: {
      email: selectedUser?.email || '',
      givenName: selectedUser?.givenName || '',
      familyName: selectedUser?.familyName || '',
      roleIdsArray: selectedUser?.roles ? deriveRoleIdsFromRoleNumber(selectedUser.roles) : [],
    },
  })

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({ queryKey: 'getSubscriptionsApiListAllPlatformAdmins' })

    if (refetchOrganisationUsers) {
      refetchOrganisationUsers() // ✅ Trigger refetch to update the parent component
    }

    toast.success(edit ? 'User successfully updated' : 'User successfully added')
    reset()
    onSubmit()
  }

  const { mutate: addUser } = putSubscriptionsApiAddUser.useMutation()
  const { mutate: updateUser } = postSubscriptionsApiUpdateUser.useMutation()

  const orgIdParam = useGetSearchParam<number>('orgId')
  const orgId = organisationId ?? orgIdParam

  const handleFormSubmit = async (data: UserForm) => {
    if (!orgId) {
      toast.error('No organisation ID found')
      return
    }

    const payload = {
      id: 0, // Default ID for new user
      active: true,
      isFrozen: false,
      teamCount: 0,
      directLinkCount: 0,
      profilePicture: selectedUser?.profilePicture ?? '',
      organisationId: orgId,
      givenName: data.givenName,
      familyName: data.familyName,
      email: data.email,
      roles: deriveRoleNumberFromRoleIds(data.roleIdsArray) as Roles,
    }

    if (edit && selectedUser?.id) {
      updateUser(
        { body: { ...payload, id: selectedUser.id } },
        {
          onSuccess: onMutationSuccess,
          onError: (error) => {
            console.log(error)
            toast.error(`Failed to update user`)
          },
        }
      )
    } else {
      addUser(
        { body: payload },
        {
          onSuccess: onMutationSuccess,
          onError: (error) => {
            console.log(error)
            toast.error(`Failed to add user`)
          },
        }
      )
    }
  }

  const handleRolesChange = useCallback(
    (updatedRoles: number[]) => {
      setValue('roleIdsArray', updatedRoles)
    },
    [setValue]
  )

  const [userRole] = useSessionStorage('roles', 1)
  const disabledRoleIds = RolesArray.filter((x) => !roleCanBeAssigned(x.roleId, userRole)).map((x) => x.roleId)
  const canEditEmail = !edit //isSuperUser(userRole) - API doesn't change email anyway so can only set on create

  return (
    <div className="form-wrapper">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Field
          label="Email"
          validationState={errors.email ? 'error' : 'none'}
          validationMessage={errors.email?.message}
        >
          <Input
            {...register('email', { required: 'Email is required', maxLength: 100 })}
            readOnly={!canEditEmail}
            disabled={!canEditEmail}
          />
        </Field>

        <Field
          label="Given Name"
          validationState={errors.givenName ? 'error' : 'none'}
          validationMessage={errors.givenName?.message}
        >
          <Input {...register('givenName', { required: 'Given name is required', maxLength: 100 })} />
        </Field>

        <Field
          label="Family Name"
          validationState={errors.familyName ? 'error' : 'none'}
          validationMessage={errors.familyName?.message}
        >
          <Input {...register('familyName', { required: 'Family name is required', maxLength: 100 })} />
        </Field>

        <RolesAssignmentComponent
          assignedRoleIds={getValues('roleIdsArray')}
          onRolesChange={handleRolesChange}
          disabledRoleIds={disabledRoleIds}
        />

        <div className="form-action-container">
          <Button text="Submit" type="submit" />
        </div>
      </form>
    </div>
  )
}
